import { useState, useEffect, useCallback, useRef } from 'react';
import eventBus from '../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';

const DETAILS_STORAGE_KEY = 'ak_visit_details_map';
const UNSEEN_STORAGE_KEY = 'ak_visit_unseen_counts';

/** Read JSON from sessionStorage, return fallback on error */
function readStorage(key, fallback = {}) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

/** Write JSON to sessionStorage */
function writeStorage(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch { /* quota exceeded — non-critical */ }
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * useMetadataNotifications — Tracks unseen metadata per visit
 *
 * Logic:
 *   • WebSocket VISIT_METADATA_ADDED events increment unseen counters
 *   • Counter ONLY counts entries from OTHER roles (not same role)
 *   • When user opens Details panel → markSeen(visitId) zeros the counter
 *   • After seeing, `hasDetails` stays true (visual distinction)
 *   • If new entries arrive after seeing → counter reappears
 *
 * Persistence:
 *   • detailsMap + unseenCounts are stored in sessionStorage so they
 *     survive page navigation within the same tab session.
 *
 * Returns:
 *   getUnseenCount(visitId)  — number of unseen entries from other roles
 *   hasDetails(visitId)      — true if visit has ANY metadata entries
 *   markSeen(visitId)        — called when Details panel opens
 *   markHasDetails(visitId)  — called when VisitActionsPanel confirms entries exist
 * ═══════════════════════════════════════════════════════════════════════
 */
export function useMetadataNotifications(currentUserRole) {
  // Initialize from sessionStorage so highlights persist across navigation
  const [unseenCounts, setUnseenCounts] = useState(() => readStorage(UNSEEN_STORAGE_KEY));
  const [detailsMap, setDetailsMap] = useState(() => readStorage(DETAILS_STORAGE_KEY));
  const roleRef = useRef(currentUserRole);
  roleRef.current = currentUserRole;

  // Sync detailsMap to sessionStorage whenever it changes
  useEffect(() => {
    writeStorage(DETAILS_STORAGE_KEY, detailsMap);
  }, [detailsMap]);

  // Sync unseenCounts to sessionStorage whenever it changes
  useEffect(() => {
    writeStorage(UNSEEN_STORAGE_KEY, unseenCounts);
  }, [unseenCounts]);

  // Listen for WebSocket metadata events
  useEffect(() => {
    const unsub = eventBus.on(REALTIME_EVENTS.VISIT_METADATA_ADDED, (event) => {
      if (event.source === 'local') return; // We added it ourselves — skip

      const { visitId, entry } = event.payload || {};
      if (!visitId) return;

      // Only count if added by a DIFFERENT role
      const addedByRole = entry?.added_by_role || '';
      if (addedByRole.toUpperCase() === (roleRef.current || '').toUpperCase()) return;

      // Increment unseen counter for this visit
      setUnseenCounts((prev) => ({
        ...prev,
        [visitId]: (prev[visitId] || 0) + 1,
      }));

      // Mark visit as having details
      setDetailsMap((prev) => ({ ...prev, [visitId]: true }));
    });

    return unsub;
  }, []);

  // Mark a visit as "seen" — counter goes to 0 but hasDetails stays
  const markSeen = useCallback((visitId) => {
    setUnseenCounts((prev) => {
      if (!prev[visitId]) return prev;
      const next = { ...prev };
      delete next[visitId];
      return next;
    });
  }, []);

  // Called by VisitActionsPanel when it confirms entries exist
  const markHasDetails = useCallback((visitId, hasEntries) => {
    setDetailsMap((prev) => {
      if (prev[visitId] === hasEntries) return prev;
      return { ...prev, [visitId]: hasEntries };
    });
  }, []);

  const getUnseenCount = useCallback(
    (visitId) => unseenCounts[visitId] || 0,
    [unseenCounts]
  );

  const hasDetails = useCallback(
    (visitId) => !!detailsMap[visitId],
    [detailsMap]
  );

  return { getUnseenCount, hasDetails, markSeen, markHasDetails };
}

export default useMetadataNotifications;
